import { Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { _setKycToken } from '@/api/auth/mutations';
import { KycSdk } from '@/app/providers/kyc-sdk';
import { ShuftiProKyc } from '@/app/providers/shuftipro-kyc';
import LockFilledGreenIcon from '@/assets/svg/ui-elements/lock-filled-green.svg';
import { Button, Modal, Separator, theme, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';

const KycProvider = {
  sumsub: 'sumsub',
  shuftiPro: 'ShuftiPro',
} as const;

const activeKycProvider = KycProvider.shuftiPro as (typeof KycProvider)[keyof typeof KycProvider];

export const KycStartModal = () => {
  const { open, handleClose } = useGlobalModal('KYC_START_MODAL');

  const [token, setToken] = useState('');
  const { mutate, isPending } = useMutation({ mutationFn: _setKycToken });

  const handleKyc = () => {
    mutate(undefined, {
      onSuccess: (data) => {
        setToken(data.token);
      },
    });
  };

  const handleModalClose = () => {
    setToken('');
    handleClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleModalClose();
      }}
      title="KYC verification"
      showClose
      maxWidth={activeKycProvider === KycProvider.shuftiPro ? 830 : 480}
    >
      <>
        <Separator tone="subtle" style={{ marginBottom: theme.spacing[24] }} />

        {!token ? (
          <>
            <Stack flexDirection="column" alignItems="center" mb="32px">
              <LockFilledGreenIcon style={{ marginBottom: '16px' }} />
              <Typography fontWeight={500}>
                Complete identity verification (KYC) and start investing
              </Typography>
            </Stack>
            <Button
              size="xl"
              fullWidth
              onClick={handleKyc}
              loading={isPending}
              loadingText="Loading..."
              style={{ marginBottom: theme.spacing[16] }}
            >
              Start KYC
            </Button>
            <Button
              color="secondary"
              variant="ghost"
              size="xl"
              fullWidth
              onClick={handleModalClose}
            >
              I will KYC later
            </Button>
          </>
        ) : activeKycProvider === KycProvider.sumsub ? (
          <KycSdk token={token} />
        ) : activeKycProvider === KycProvider.shuftiPro ? (
          <ShuftiProKyc verificationUrl={token} />
        ) : null}
      </>
    </Modal>
  );
};
